import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface NavigationState {
  currentStep: number;
  completedSteps: number[];
  lastActiveTime: string;
  sessionStartTime: string;
  incidentInProgress: boolean;
}

const initialState: NavigationState = {
  currentStep: 0,
  completedSteps: [],
  lastActiveTime: new Date().toISOString(),
  sessionStartTime: new Date().toISOString(),
  incidentInProgress: false
};

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
      state.lastActiveTime = new Date().toISOString();
      state.incidentInProgress = true;
    },
    
    markStepCompleted: (state, action: PayloadAction<number>) => {
      if (!state.completedSteps.includes(action.payload)) {
        state.completedSteps.push(action.payload);
      }
      state.lastActiveTime = new Date().toISOString();
    },
    
    resetNavigation: (state) => {
      state.currentStep = 0;
      state.completedSteps = [];
      state.incidentInProgress = false;
      state.lastActiveTime = new Date().toISOString();
    },
    
    updateLastActiveTime: (state) => {
      state.lastActiveTime = new Date().toISOString();
    },
    
    setIncidentInProgress: (state, action: PayloadAction<boolean>) => {
      state.incidentInProgress = action.payload;
      if (action.payload) {
        state.sessionStartTime = new Date().toISOString();
      }
    },
    
    jumpToStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
      state.lastActiveTime = new Date().toISOString();
    }
  }
});

export const {
  setCurrentStep,
  markStepCompleted,
  resetNavigation,
  updateLastActiveTime,
  setIncidentInProgress,
  jumpToStep
} = navigationSlice.actions;

export default navigationSlice.reducer;

// Selectors
export const selectCurrentStep = (state: { navigation: NavigationState }) => 
  state.navigation.currentStep;

export const selectCompletedSteps = (state: { navigation: NavigationState }) => 
  state.navigation.completedSteps;

export const selectIsStepCompleted = (state: { navigation: NavigationState }, step: number) =>
  state.navigation.completedSteps.includes(step);

export const selectIncidentInProgress = (state: { navigation: NavigationState }) =>
  state.navigation.incidentInProgress;

export const selectSessionDuration = (state: { navigation: NavigationState }) => {
  const start = new Date(state.navigation.sessionStartTime).getTime();
  const now = Date.now();
  return now - start;
};

export const selectIsSessionExpired = (state: { navigation: NavigationState }, expiryHours: number = 24) => {
  const lastActive = new Date(state.navigation.lastActiveTime).getTime();
  const now = Date.now();
  const hoursInactive = (now - lastActive) / (1000 * 60 * 60);
  return hoursInactive > expiryHours;
};